import React, { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';

// One labelled input for the sign-in screens.
//
//  - the label is tied to the input (htmlFor / id), in sentence case, with "(optional)" only on
//    optional fields;
//  - the hint (persistent helper) and the error are read out with the field (aria-describedby),
//    and an error marks the field invalid and sits right under it;
//  - `invalid` + `describedBy` mark a field wrong when the message lives elsewhere (a sign-in
//    alert that is about both email and password);
//  - a password field gets a real show/hide toggle: a 36px button with a fixed name and
//    aria-pressed, so a screen reader hears "Show password, pressed" when the text is visible.
const AuthField = React.forwardRef(function AuthField({
  label, hint, error, invalid = false, describedBy: moreDescribedBy, optional = false, icon: Icon,
  action, type = 'text', id: idProp, className, inputClassName, ...input
}, ref) {
  const auto = useId();
  const id = idProp || `f${auto.replace(/:/g, '')}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const isPassword = type === 'password';
  const [show, setShow] = useState(false);
  const bad = !!error || invalid;
  const describedBy = [errorId, moreDescribedBy, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={className}>
      <div className="flex items-end justify-between gap-2 mb-1.5">
        <label htmlFor={id} className="text-[13px] font-medium text-slate-700">
          {label}
          {optional && <span className="font-normal text-slate-500"> (optional)</span>}
        </label>
        {action}
      </div>
      <div className="relative">
        {Icon && (
          <Icon size={15} aria-hidden="true"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        )}
        <input
          ref={ref}
          id={id}
          type={isPassword && show ? 'text' : type}
          aria-invalid={bad ? true : undefined}
          aria-describedby={describedBy}
          className={clsx(
            'input-field',
            Icon && 'pl-9',
            isPassword && 'pr-12',
            bad && '!border-red-400',
            inputClassName,
          )}
          {...input}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            aria-label="Show password"
            aria-pressed={show}
            aria-controls={id}
            title={show ? 'Hide password' : 'Show password'}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-150"
          >
            {show ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
          </button>
        )}
      </div>
      {hint && <p id={hintId} className="mt-1.5 text-xs text-slate-500">{hint}</p>}
      {error && <p id={errorId} role="alert" className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
});

export default AuthField;
